import React from 'react';
import { motion } from 'framer-motion';
import { Apple, Play, Star, TrendingDown } from 'lucide-react';
import Button from './ui/Button';
import { fadeInUp, stagger } from '../lib/animations';

// Mockup do app Gas2 com screenshot real e animação de flutuação
const PhoneMockup: React.FC = () => (
  <div className="relative mx-auto w-[280px] sm:w-[300px]">
    {/* Brilho ambiente atrás do celular */}
    <div className="absolute inset-x-6 bottom-0 h-28 rounded-full bg-amber-300/35 blur-2xl pointer-events-none" />

    <motion.div
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
      className="relative z-10"
    >
      {/* Corpo do celular */}
      <div className="rounded-[2.8rem] bg-gray-900 p-[10px] shadow-2xl shadow-gray-900/60 ring-1 ring-white/10">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 z-20 flex h-7 w-24 -translate-x-1/2 items-end justify-center rounded-b-2xl bg-gray-900 pb-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-gray-800" />
        </div>

        {/* Tela com screenshot real */}
        <div className="overflow-hidden rounded-[2.4rem]" style={{ height: 560 }}>
          <img
            src="/screenshots/tela-home.webp"
            alt="Painel principal do Gas2"
            className="h-full w-full object-cover object-top"
          />
        </div>
      </div>
    </motion.div>
  </div>
);

// Seção hero com layout em duas colunas (texto à esquerda, mockup à direita)
const Hero: React.FC = () => (
  <section className="relative min-h-[92vh] overflow-hidden bg-white pt-24 pb-16 sm:pt-28 lg:flex lg:items-center lg:pt-20 lg:pb-20">
    {/* Decorações de fundo */}
    <div className="pointer-events-none absolute top-0 right-0 h-[600px] w-[600px] -translate-y-1/3 translate-x-1/4 rounded-full bg-amber-50 opacity-80 blur-3xl" />
    <div className="pointer-events-none absolute bottom-0 left-0 h-[400px] w-[400px] translate-y-1/3 -translate-x-1/4 rounded-full bg-amber-50 opacity-60 blur-3xl" />

    <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col-reverse items-center gap-12 lg:flex-row lg:gap-16">

        {/* Coluna esquerda — conteúdo textual */}
        <motion.div
          className="w-full flex-1 text-center lg:text-left"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          {/* Badge de destaque */}
          <motion.div variants={fadeInUp} className="mb-6 flex justify-center lg:justify-start">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-4 py-1.5 text-xs font-semibold text-amber-700">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
              Disponível para download imediato no Android e iOS.
            </span>
          </motion.div>

          {/* Título principal */}
          <motion.h1
            variants={fadeInUp}
            className="mx-auto max-w-xl text-4xl font-bold leading-[1.1] tracking-tight text-gray-900 sm:text-5xl lg:mx-0 lg:max-w-none lg:text-[3.5rem]"
          >
            Controle total{' '}
            <span className="relative inline-block">
              <span className="relative z-10">do seu veículo</span>
              <span className="absolute bottom-1 left-0 right-0 z-0 -skew-x-2 h-3.5 bg-amber-300/55 rounded-sm" />
            </span>
          </motion.h1>

          {/* Subtítulo persuasivo */}
          <motion.p
            variants={fadeInUp}
            className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-gray-500 sm:text-lg lg:mx-0"
          >
            Controle seus gastos com combustível, compare etanol&nbsp;×&nbsp;gasolina em tempo
            real e nunca mais pague caro no posto.
          </motion.p>

          {/* Botões CTA das lojas */}
          <motion.div
            variants={fadeInUp}
            className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start"
          >
            <Button
              href="https://play.google.com/store/apps/details?id=info.marcussoftware.gas2&hl=pt_BR&utm_source=lp&utm_medium=hero&utm_campaign=gas2_download"
              variant="dark"
              size="lg"
              icon={Play}
              target="_blank"
            >
              Google Play
            </Button>
            <Button
              href="https://apps.apple.com/br/app/gas2-controle-de-combust%C3%ADvel/id6755345109?utm_source=lp&utm_medium=hero&utm_campaign=gas2_download"
              variant="outline"
              size="lg"
              icon={Apple}
              className="!bg-white !text-gray-900 !border-gray-200 hover:!border-amber-400 hover:!bg-amber-50"
              target="_blank"
            >
              App Store
            </Button>
          </motion.div>

          {/* Prova social */}
          <motion.div
            variants={fadeInUp}
            className="mt-10 flex flex-col items-center justify-center gap-5 text-sm text-gray-500 sm:flex-row lg:justify-start"
          >
            <div className="flex items-center gap-1.5">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              <span>
                <strong className="text-gray-900">4,9/5,0</strong>{' '}
                <span className="text-gray-400">na Google Play</span>
              </span>
            </div>
            <div className="hidden sm:block h-4 w-px bg-gray-200" />
            <div className="flex items-center gap-1.5">
              <TrendingDown className="h-4 w-4 text-emerald-500" strokeWidth={2} />
              <span>
                <strong className="text-gray-900">+10.000</strong> Abastecimentos Inteligentes Calculados
              </span>
            </div>
          </motion.div>
        </motion.div>

        {/* Coluna direita — mockup do app */}
        <motion.div
          className="flex w-full flex-1 justify-center lg:justify-end"
          initial={{ opacity: 0, x: 36 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
        >
          <PhoneMockup />
        </motion.div>
      </div>
    </div>
  </section>
);

export default Hero;

